import { styled } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { flexRender } from '@tanstack/react-table';
import { Checkbox } from '../../../Inputs';
import { getCellAlign } from '../../cell/align';
import { useDataProvider } from '../../DataProviderContext';
import { useIsFilterVisible } from '../../filter/FilterContext';
import { TableHeadFilter } from '../../filter/TableHeadFilter';
import type { TableColumn, TableHeaderGroup } from '../../tanstackTypes';
import { HeadCell } from './HeadCell';
import { Sort } from './Sort';

const PlaceholderHeadCell = styled(TableCell, {
  name: 'HeadRow',
  slot: 'PlaceholderHeadCell',
})(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: 0,
}));

interface HeadRowProps {
  headerGroup: TableHeaderGroup<any>;
}

const getCanSort = (column: TableColumn<any>) =>
  typeof column.getCanSort === 'function' && column.getCanSort();

const getCanFilter = (column: TableColumn<any>) =>
  typeof column.getCanFilter === 'function' && column.getCanFilter();

export const HeadRow = ({ headerGroup }: HeadRowProps) => {
  const { table, isRowIndeterminate } = useDataProvider();
  const [isFilterVisible] = useIsFilterVisible();
  const enableRowSelection = Boolean(table?.options.enableRowSelection);
  const enableSorting = Boolean(table?.options.enableSorting);

  if (!table) {
    return null;
  }

  const isAllRowsSelected =
    enableRowSelection && typeof table.getIsAllRowsSelected === 'function'
      ? table.getIsAllRowsSelected()
      : false;
  const isSomeRowsSelected =
    enableRowSelection &&
    !isAllRowsSelected &&
    table
      .getRowModel()
      .rows.some(
        (row) =>
          (typeof row.getIsSelected === 'function' && row.getIsSelected()) ||
          (isRowIndeterminate?.(row) ?? false)
      );

  return (
    <TableRow key={headerGroup.id}>
      {enableRowSelection && (
        <HeadCell padding="checkbox" sx={{ verticalAlign: 'middle' }}>
          <Checkbox
            indeterminate={isSomeRowsSelected}
            checked={isAllRowsSelected}
            onClick={() => {
              table?.toggleAllRowsSelected();
            }}
          />
        </HeadCell>
      )}
      {headerGroup.headers.map((header) => {
        if (header.isPlaceholder) {
          return <PlaceholderHeadCell key={header.id} />;
        }
        const canSort = enableSorting && getCanSort(header.column);
        const canFilter = getCanFilter(header.column);
        const align = getCellAlign(header.column);

        return (
          <HeadCell
            sortable={canSort}
            colSpan={header.colSpan}
            columnGroup={header.subHeaders.length > 0}
            key={header.id}
            align={align}
          >
            {isFilterVisible && canFilter && (
              <TableHeadFilter column={header.column} />
            )}
            <div
              onClick={
                canSort &&
                typeof header.column.getToggleSortingHandler === 'function'
                  ? header.column.getToggleSortingHandler()
                  : undefined
              }
            >
              {canSort && align === 'right' && <Sort column={header.column} />}
              {flexRender(header.column.columnDef.header, header.getContext())}
              {canSort && align !== 'right' && <Sort column={header.column} />}
            </div>
          </HeadCell>
        );
      })}
    </TableRow>
  );
};
